import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { DraggableTree } from '@framework/ui/components/tree/DraggableTree.tsx';
import { Space, Typography } from 'antd';
import { useWatch } from 'react-hook-form';
import { type IMapGroup } from '../../layers/MapGroup.ts';
import { type IMapLayer, type MapLayerType } from '../../layers/MapLayer.ts';
import { MapWidgetLogic } from '../../MapWidgetLogic.ts';
import { CadastralPresetButton } from '../cadastral/CadastralPresetButton.tsx';
import { LayerCatalogImportButton } from '../catalog/LayerCatalogImportButton.tsx';
import { MapGroupConfigDrawerButton } from '../MapGroupConfigDrawerButton.tsx';
import { MapLayerConfigDrawerButton } from '../MapLayerConfigDrawerButton.tsx';
import { type IConfigTreeNodeData } from './buildConfigTreeData.ts';
import { useMapTreeConfig } from './useMapTreeConfig.tsx';

export interface IMapGroupsAndLayersTreeSectionProps {
  groupEditor: RelationEditor<IMapGroup>;
  layersEditor: RelationEditor<IMapLayer>;
  allowedTypes?: MapLayerType[];
  /**
   * Mostra il bottone "Importa da Catalogo". Disabilitarlo quando la
   * sezione è usata per editare un catalogo (import catalogo-in-catalogo).
   */
  showCatalogImport?: boolean;
}

export function MapGroupsAndLayersTreeSection(
  props: IMapGroupsAndLayersTreeSectionProps,
) {
  const { groupEditor, layersEditor, showCatalogImport = true } = props;
  const groups: IMapGroup[] = useWatch({ name: 'groups' }) ?? [];

  const {
    treeData,
    expandedKeys,
    onExpandedKeysChange,
    validationContext,
    handleDrop,
    handleEdit,
    handleDelete,
    nodeIcon,
    nodeExtraActions,
    allowDrop,
  } = useMapTreeConfig({ groupEditor, layersEditor });

  return (
    <div>
      <Typography.Paragraph>
        Trascina i gruppi e i livelli per riordinarli. Il livello che appare per
        primo è quello più in alto, ovvero il livello in <b>primo piano</b>.
      </Typography.Paragraph>

      {treeData.length > 0 && (
        <DraggableTree<IConfigTreeNodeData>
          treeData={treeData}
          expandedKeys={expandedKeys}
          onExpandedKeysChange={onExpandedKeysChange}
          onDropRaw={handleDrop}
          onEdit={handleEdit}
          onDelete={handleDelete}
          nodeIcon={nodeIcon}
          nodeExtraActions={nodeExtraActions}
          allowDrop={allowDrop}
        />
      )}

      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <MapGroupConfigDrawerButton
          groupEditor={groupEditor}
          layersEditor={layersEditor}
          validationContext={validationContext}
        />
        <MapLayerConfigDrawerButton
          editor={layersEditor}
          allowedTypes={props.allowedTypes}
          groups={groups}
        />
        {MapWidgetLogic.isCadastralEnabled() && (
          <CadastralPresetButton editor={layersEditor} />
        )}
        {showCatalogImport && (
          <LayerCatalogImportButton
            groupEditor={groupEditor}
            layersEditor={layersEditor}
          />
        )}
      </Space>
    </div>
  );
}
